import { useEffect, useRef } from 'react';

/**
 * OverlayCrawlBanner - JS-driven scrolling crawl text with blue/gold broadcast theme
 */

// Shared scroll position so the crawl continues seamlessly when it moves between
// the full-width banner and the studio info strip (never restarts on view switches)
export const sharedCrawlPos = { x: null, text: '' };

export function OverlayCrawlBanner({ showCrawl, crawlText }) {
  const crawlRef = useRef(null);
  const crawlFrameRef = useRef(null);
  const crawlLastTime = useRef(null);

  useEffect(() => {
    if (!showCrawl || !crawlText) {
      if (crawlFrameRef.current) cancelAnimationFrame(crawlFrameRef.current);
      crawlFrameRef.current = null;
      crawlLastTime.current = null;
      return;
    }
    if (sharedCrawlPos.text !== crawlText) {
      sharedCrawlPos.x = null;
      sharedCrawlPos.text = crawlText;
    }
    const speed = 80;
    const animate = (ts) => {
      if (!crawlRef.current) { crawlFrameRef.current = requestAnimationFrame(animate); return; }
      if (!crawlLastTime.current) crawlLastTime.current = ts;
      const dt = (ts - crawlLastTime.current) / 1000;
      crawlLastTime.current = ts;
      const el = crawlRef.current;
      const textW = el.scrollWidth;
      const screenW = window.innerWidth;
      if (sharedCrawlPos.x === null) sharedCrawlPos.x = screenW;
      sharedCrawlPos.x -= speed * dt;
      if (sharedCrawlPos.x < -textW) sharedCrawlPos.x = screenW;
      el.style.transform = `translateX(${sharedCrawlPos.x}px)`;
      crawlFrameRef.current = requestAnimationFrame(animate);
    };
    crawlFrameRef.current = requestAnimationFrame(animate);
    return () => { if (crawlFrameRef.current) cancelAnimationFrame(crawlFrameRef.current); };
  }, [showCrawl, crawlText]);

  if (!showCrawl || !crawlText) return null;

  const parts = crawlText.split(/((?:V\d+|DD)-\d+:)/g);
  const renderedText = parts.map((part, i) =>
    /^(?:V\d+|DD)-\d+:$/.test(part)
      ? <span key={i} style={{ color: 'var(--theme-accent)', fontWeight: 700 }}>{part}</span>
      : part
  );

  return (
    <div
      data-testid="crawl-banner"
      className="crawl-banner"
      style={{
        position: 'absolute',
        bottom: '20px',
        left: 0,
        width: '100%',
        height: '48px',
        backgroundColor: '#050F23',
        backgroundSize: 'cover',
        backgroundPosition: 'center bottom',
        overflow: 'hidden',
        zIndex: 100,
        borderTop: '2px solid var(--theme-panel-border)',
        borderBottom: '2px solid var(--theme-panel-border)',
      }}
    >
      <div
        ref={crawlRef}
        className="crawl-text"
        style={{
          position: 'absolute',
          whiteSpace: 'nowrap',
          color: '#FFFFFF',
          fontSize: '34px',
          lineHeight: '48px',
          fontFamily: "'Inter', sans-serif",
          fontWeight: 500,
          willChange: 'transform',
          zIndex: 1,
        }}
      >
        {renderedText}
      </div>
    </div>
  );
}
